import { IconArrowLeft, IconMoon, IconSun } from "@tabler/icons-react";
import { Link, Outlet } from "@tanstack/react-router";
import { useContext } from "react";
import {
	ThemeContext,
	type ThemeContextInterface,
} from "@/components/providers/theme-provider";
import { SiteLogo } from "@/components/site-logo";
import { Button, buttonVariants } from "@/components/ui/button";
import { config } from "@/data/config";
import { useHeaderScrolled } from "@/hooks/use-header-scrolled";
import { getSiteHeaderChromeClass } from "@/lib/site-header-chrome";
import { cn } from "@/lib/utils";

export function AuthLayout() {
	const { darkTheme, toggleTheme } = useContext(
		ThemeContext,
	) as ThemeContextInterface;
	const scrolled = useHeaderScrolled();

	return (
		<div className="flex min-h-dvh flex-col bg-background text-foreground">
			<header className={getSiteHeaderChromeClass(scrolled)}>
				<div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-4 sm:h-16 sm:px-6 lg:px-8">
					<Link
						to="/"
						className="flex min-w-0 items-center gap-2.5 rounded-full outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						<SiteLogo className="h-7 w-auto shrink-0 sm:h-8" aria-hidden />
						<span className="truncate text-base font-semibold tracking-tight text-foreground sm:text-lg">
							{config.siteName}
						</span>
					</Link>

					<div className="flex shrink-0 items-center gap-1 sm:gap-2">
						<Link
							to="/"
							aria-label="Back to site"
							className={cn(
								buttonVariants({ variant: "ghost", size: "icon" }),
								"rounded-full text-muted-foreground sm:hidden",
							)}
						>
							<IconArrowLeft className="size-5 shrink-0" aria-hidden />
						</Link>
						<Link
							to="/"
							className={cn(
								buttonVariants({ variant: "ghost", size: "sm" }),
								"hidden gap-1.5 rounded-full px-3 text-muted-foreground sm:inline-flex",
							)}
						>
							<IconArrowLeft className="size-4 shrink-0" aria-hidden />
							<span className="text-sm font-medium">Back to site</span>
						</Link>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="shrink-0 rounded-full text-muted-foreground"
							onClick={toggleTheme}
							aria-label={`Switch to ${darkTheme ? "light" : "dark"} theme`}
						>
							{darkTheme ? (
								<IconMoon className="size-5" strokeWidth={2} />
							) : (
								<IconSun className="size-5" strokeWidth={2} />
							)}
						</Button>
					</div>
				</div>
			</header>
			<Outlet />
		</div>
	);
}
